import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

import { useUrlStateV2 } from 'hooks';

import { InvoiceModal } from '../components';

interface InvoiceContext {
  openInvoice(id: string): void;
  invoice: string | null;
}

interface ContextProps {
  children: ReactNode;
}

const invoiceContext = createContext({} as InvoiceContext);

export function InvoiceProvider({ children }: ContextProps): JSX.Element {
  const [{ invoice }, setUrlQuery] = useUrlStateV2({
    invoice: '',
  });

  const openInvoice = useCallback(
    (id: string) => {
      setUrlQuery({ invoice: id });
    },
    [setUrlQuery],
  );

  const closeInvoice = useCallback(() => {
    setUrlQuery({ invoice: null });
  }, [setUrlQuery]);

  const data = useMemo(
    () => ({
      invoice: invoice || null,
      openInvoice,
    }),
    [invoice, openInvoice],
  );

  return (
    <invoiceContext.Provider value={data}>
      <InvoiceModal close={closeInvoice} invoice={invoice} />

      {children}
    </invoiceContext.Provider>
  );
}

export function useInvoice(): InvoiceContext {
  const context = useContext(invoiceContext);

  if (!context.openInvoice) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }

  return context;
}
